import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Checkbox from '../Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Form from '../Form';
import FormDrawer from '../FormDrawer';
import TextField from '../TextField';
import Icon from '../Icon';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import stripSpecialCharacters from '@afosto/utils/string/stripSpecialCharacters';
import * as defaultRenderers from '../FiltersField/renderers';
import { saveViewValidationSchema } from './validations';
import translations from './translations';
import styled from 'styled-components';

const InfoContainer = styled.div`
  && {
    padding: ${props => props.theme.spacing()}px;
    margin-top: ${props => props.theme.spacing()}px;

    > div {
      margin-bottom: ${props => props.theme.spacing()}px;
    }
  }
`;

const DataGridSaveView = ({ filterRenderers }) => {
  const intl = useIntl();
  const [showViewInformation, setShowViewInformation] = useState(false);
  const {
    showSaveViewSelection,
    setShowSaveViewSelection,
    setShowViewsSelection,
    views: viewsModule = {},
    dataProperties = {},
    filters: filtersModule = {},
    pagination: paginationModule = {},
    sorting = {},
  } = useDataSourceContext();
  const {
    canCreateViewForTenant,
    defaultView,
    getActiveView,
    getView,
    selectedViewSelection,
    setSelectedViewSelection,
    createView,
    updateView,
  } = viewsModule;
  const activeView = getActiveView();
  const { properties = [], customProperties = [], selectedProperties = [] } = dataProperties;
  const { filters = [], activeFilters = [] } = filtersModule;
  const { activeSort = [] } = sorting;
  const { itemsPerPage } = paginationModule;
  const renderers = { ...defaultRenderers.default, ...filterRenderers };

  /**
   * Save the view.
   */
  const saveView = async ({ name: viewName, saveViewForTenant }) => {
    if (viewName) {
      const type = saveViewForTenant ? 'tenant' : 'user';
      const viewKey = selectedViewSelection ? selectedViewSelection.key : activeView.key;
      const view = getView(viewKey);
      const sanitizedViewName = stripSpecialCharacters(viewName.toLowerCase());
      const sameView = selectedViewSelection
        ? true
        : view &&
          stripSpecialCharacters(view.name.toLowerCase()) === sanitizedViewName &&
          view.type === type;

      if (view && sameView) {
        let viewType = view.type;
        let viewSuffix = '';

        if (!selectedViewSelection) {
          viewType = type;
        }

        if (viewType === 'user') {
          viewSuffix = `(${intl.formatMessage(translations.views.label.private)})`;
        }

        const updatedView = {
          ...view,
          type: view.type,
          label: `${viewName} ${viewSuffix}`.trim(),
          name: viewName,
        };

        if (selectedViewSelection) {
          await updateView(updatedView);
        } else {
          await updateView({
            ...updatedView,
            customProperties: [...customProperties],
            properties: [...selectedProperties],
            filters: [...activeFilters],
            sort: [...activeSort],
            itemsPerPage,
          });
        }
      } else {
        const generatedViewKey = `${
          saveViewForTenant ? 'me.' : ''
        }${sanitizedViewName}-${Date.now()}`;
        let viewLabel = '';

        if (!saveViewForTenant) {
          viewLabel = `(${intl.formatMessage(translations.views.label.private)})`;
        }

        const newView = {
          key: generatedViewKey,
          name: viewName,
          label: `${viewName} ${viewLabel}`.trim(),
          description: viewName,
          type,
          customProperties: [...customProperties],
          properties: [...selectedProperties],
          filters: [...activeFilters],
          sort: [...activeSort],
          itemsPerPage,
          active: true,
        };

        await createView(newView);
      }

      setSelectedViewSelection(null);
      setShowSaveViewSelection(false);
    }
  };

  const form = useForm({
    validationSchema: saveViewValidationSchema,
    defaultValues: {
      name: '',
      saveViewForTenant: false,
    },
    onSubmit: async data => {
      await saveView(data);
    },
    onCancel: () => {
      setShowViewInformation(false);

      if (selectedViewSelection) {
        setShowViewsSelection(true);
      }

      setSelectedViewSelection(null);
      setShowSaveViewSelection(false);
    },
  });
  const { setValue } = form;

  /**
   * Close the view save drawer when clicking on close drawer.
   */
  const handleClose = () => {
    setShowViewInformation(false);
    setSelectedViewSelection(null);
    setShowSaveViewSelection(false);
  };

  /**
   * Return the headings of the selected columns in the view as text.
   * @returns {string}
   */
  const getViewPropertiesHeadings = viewProperties =>
    viewProperties
      .reduce((acc, selectedProperty) => {
        const property = [...properties, ...customProperties].find(
          ({ key: columnKey }) => columnKey === selectedProperty,
        );

        if (property) {
          return [...acc, property.name];
        }

        return acc;
      }, [])
      .join(', ');

  /**
   * Return the view filter selections as text.
   * @returns {string}
   */
  const getViewFiltersHeadings = viewFilters =>
    viewFilters.reduce((acc, selectedFilter) => {
      const filter = filters.find(({ key: filterKey }) => filterKey === selectedFilter.key);

      if (filter) {
        const { key, name: filterName, type } = filter;
        const { operator, value } = selectedFilter;
        const ValueRenderer = renderers[type] ?? renderers.default;

        return [
          ...acc,
          <ValueRenderer key={key} name={filterName} operator={operator} value={value} />,
        ];
      }

      return acc;
    }, []);

  /**
   * Return the view sorting as text.
   * @returns {string}
   */
  const getViewSortingHeadings = viewSort =>
    viewSort
      .reduce((acc, activeSorting) => {
        const property = [...properties, ...customProperties].find(
          ({ key: propertyKey, sortKey }) => (sortKey || propertyKey) === activeSorting.key,
        );
        const direction = intl.formatMessage(translations.sortDirections[activeSorting.direction]);

        if (property) {
          return [...acc, `${property.name} ${direction.toLowerCase()}`];
        }

        return acc;
      }, [])
      .join(', ');

  /**
   * Toggle the visibility of the view information.
   */
  const handleShowViewInformation = () => {
    setShowViewInformation(!showViewInformation);
  };

  const summaryViewProperties = getViewPropertiesHeadings(
    selectedViewSelection
      ? selectedViewSelection.properties.map(property => property.key)
      : selectedProperties.map(property => property.key),
  );
  const summaryViewFilters = getViewFiltersHeadings(
    selectedViewSelection ? selectedViewSelection.filters : activeFilters,
  );
  const summaryViewSorting = getViewSortingHeadings(
    selectedViewSelection ? selectedViewSelection.sort : activeSort,
  );

  /**
   * Return the helper text for the name field.
   * @returns {string|null}
   */
  const getNameHelperText = () => {
    const isDefault = activeView && activeView.key === defaultView.key;

    return selectedViewSelection || isDefault
      ? null
      : intl.formatMessage(translations.saveView.inputs.name.helperText);
  };

  useEffect(() => {
    if (!showSaveViewSelection) {
      setValue('name', '');
    }
  }, [showSaveViewSelection, setValue]);

  /**
   * Initialize the view settings based on the selected view selection.
   */
  useEffect(() => {
    if (selectedViewSelection) {
      setValue('name', selectedViewSelection.name);
      setValue('saveViewForTenant', selectedViewSelection.type === 'tenant');
    } else if (activeView) {
      const isDefault = defaultView.key === activeView.key;

      setValue('name', isDefault ? '' : activeView.name);
      setValue('saveViewForTenant', isDefault ? false : activeView.type === 'tenant');
    }
  }, [selectedViewSelection, activeView, defaultView, showSaveViewSelection, setValue]);

  return (
    <FormDrawer
      isOpen={showSaveViewSelection}
      onClose={handleClose}
      title={
        selectedViewSelection
          ? selectedViewSelection.name
          : intl.formatMessage(translations.saveView.title)
      }
      drawerWidth="440px"
    >
      <LoadingWrapper isLoading={form.isSubmitting}>
        <Form form={form}>
          <TextField
            name="name"
            label={intl.formatMessage(translations.saveView.inputs.name.label)}
            helperText={getNameHelperText()}
            inputProps={{
              autoComplete: 'off',
            }}
            autoFocus
          />
          {!selectedViewSelection && canCreateViewForTenant && (
            <Checkbox
              name="saveViewForTenant"
              label={intl.formatMessage(translations.saveView.inputs.saveViewForTenant.label)}
            />
          )}
          <div>
            <Button color="primary" onClick={handleShowViewInformation} style={{ marginTop: 30 }}>
              <FormattedMessage
                {...translations.saveView.showViewInformation.button[
                  showViewInformation ? 'hide' : 'show'
                ]}
              />
              <Icon icon={['fal', showViewInformation ? 'angle-up' : 'angle-down']} />
            </Button>
          </div>
          <Collapse in={showViewInformation}>
            <InfoContainer>
              <div>
                <strong>
                  <FormattedMessage {...translations.saveView.showViewInformation.columns} />
                </strong>
                : {summaryViewProperties.length > 0 ? summaryViewProperties : '-'}
              </div>
              <div>
                <strong>
                  <FormattedMessage {...translations.saveView.showViewInformation.filters} />
                </strong>
                : {summaryViewFilters.length > 0 ? summaryViewFilters : '-'}
              </div>
              <div>
                <strong>
                  <FormattedMessage {...translations.saveView.showViewInformation.sorting} />
                </strong>
                : {summaryViewSorting.length > 0 ? summaryViewSorting : '-'}
              </div>
              <div>
                <strong>
                  <FormattedMessage {...translations.saveView.showViewInformation.itemsPerPage} />
                </strong>
                : {itemsPerPage ?? '-'}
              </div>
            </InfoContainer>
          </Collapse>
        </Form>
      </LoadingWrapper>
    </FormDrawer>
  );
};

DataGridSaveView.propTypes = {
  filterRenderers: PropTypes.object,
};

DataGridSaveView.defaultProps = {
  filterRenderers: {},
};

export default DataGridSaveView;
